import {createSlice} from '@reduxjs/toolkit';
import {getUserResourcesFailure, getUserResourcesRequest, getUserResourcesSuccess} from 'src/openapi/admin/apis/UserAccessApi';
import {getCurrentUserFailure, getCurrentUserRequest, getCurrentUserSuccess} from 'src/openapi/admin/apis/UserIdentityApi';
import {getUserToolsFailure, getUserToolsRequest, getUserToolsSuccess} from 'src/openapi/admin/apis/UserToolsApi';
import {AccessManagementTool} from 'src/openapi/admin/models/AccessManagementTool';
import {User} from 'src/openapi/admin/models/User';

import type {Locale} from '@pb/kit-context-locale';
import type {AppState} from 'src/store/typedef';


type LoadStatus = 'idle' | 'loading' | 'succeeded' | 'failed' | 'unauthorized';

type InitialState = {
    locale: Locale | null,
    idToken: string | null,
    accessToken: string | null,
    user: User | null,
    resources: Array<string>,
    userTools: Array<AccessManagementTool>,
    currentUserStatus: LoadStatus,
    resourcesStatus: LoadStatus,
    userToolsStatus: LoadStatus,
};

export const initialState: InitialState = {
    locale: null,
    idToken: null,
    accessToken: null,
    user: null,
    resources: [],
    userTools: [],
    currentUserStatus: 'idle',
    resourcesStatus: 'idle',
    userToolsStatus: 'idle',
};

export const userInfoSlice = createSlice({
    name: 'userInfo',
    initialState,
    reducers: {
        setLocale: (state, action) => {
            state.locale = action.payload;
        },
        setIdToken: (state, action) => {
            state.idToken = action.payload;
        },
        setAccessToken: (state, action) => {
            state.accessToken = action.payload;
        },
        setResources: (state, action) => {
            state.resources = action.payload;
            state.resourcesStatus = 'succeeded';
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getCurrentUserRequest, state => {
                state.currentUserStatus = 'loading';
                state.resourcesStatus = 'idle';
                state.userToolsStatus = 'idle';
                state.user = null;
                state.resources = [];
                state.userTools = [];
            })
            .addCase(getCurrentUserSuccess, (state, action) => {
                state.currentUserStatus = 'succeeded';
                state.user = action.payload.data;
            })
            .addCase(getCurrentUserFailure, state => {
                state.currentUserStatus = 'unauthorized';
                state.user = null;
                state.resources = [];
                state.userTools = [];
                state.resourcesStatus = 'idle';
                state.userToolsStatus = 'idle';
            })
            .addCase(getUserResourcesRequest, state => {
                state.resourcesStatus = 'loading';
            })
            .addCase(getUserResourcesSuccess, (state, action) => {
                state.resourcesStatus = 'succeeded';
                state.resources = action.payload.data ?? [];
            })
            .addCase(getUserResourcesFailure, state => {
                state.resourcesStatus = 'failed';
                state.resources = [];
            })
            .addCase(getUserToolsRequest, state => {
                state.userToolsStatus = 'loading';
            })
            .addCase(getUserToolsSuccess, (state, action) => {
                state.userToolsStatus = 'succeeded';
                state.userTools = action.payload.data ?? [];
            })
            .addCase(getUserToolsFailure, state => {
                state.userToolsStatus = 'failed';
                state.userTools = [];
            });
    },
});

export const {setLocale, setIdToken, setAccessToken, setResources} = userInfoSlice.actions;

export const selectUserTools = (state: AppState) => state.userInfo.userTools;
export const selectUserToolsStatus = (state: AppState) => state.userInfo.userToolsStatus;
export const selectLocale = (state: AppState) => state.userInfo.locale;
export const selectIdToken = (state: AppState) => state.userInfo.idToken;
export const selectAccessToken = (state: AppState) => state.userInfo.accessToken;
export const selectCurrentUser = (state: AppState) => state.userInfo.user;
export const selectUserResources = (state: AppState) => state.userInfo.resources;
export const selectCurrentUserStatus = (state: AppState) => state.userInfo.currentUserStatus;
export const selectUserResourcesStatus = (state: AppState) => state.userInfo.resourcesStatus;
